import { Trans } from "@lingui/macro";
import { useCallback } from "react";
import { Link } from "react-router-dom";
import { zeroAddress } from "viem";

import { ContractsChainId, getChainName, getViemChain, SourceChainId } from "config/chains";
import { JUMPER_BRIDGE_URL } from "config/links";
import { useGmxAccountDepositViewTokenAddress, useGmxAccountModalOpen } from "context/GmxAccountContext/hooks";
import { ValidationBannerErrorName } from "domain/synthetics/trade/utils/validation";
import { useGasPaymentTokensText } from "lib/gas/useGasPaymentTokensText";
import { useLocalizedList } from "lib/i18n";
import { getGasPaymentTokens } from "sdk/configs/express";
import { convertTokenAddress, getToken } from "sdk/configs/tokens";

import { ColorfulButtonLink } from "components/ColorfulBanner/ColorfulBanner";
import ExternalLink from "components/ExternalLink/ExternalLink";
import { InsufficientWntBanner } from "components/GmxAccountModal/InsufficientWntBanner";

export function InsufficientNativeTokenBalanceMessage({ chainId }: { chainId: ContractsChainId }) {
  const nativeToken = getToken(chainId, zeroAddress);

  if (!nativeToken) {
    return null;
  }

  const nativeTokenSymbol = nativeToken.symbol;

  return (
    <div>
      <Trans>
        Insufficient {nativeTokenSymbol} for gas on {getChainName(chainId)}.{" "}
        <Link className="underline underline-offset-2" to={`/trade/swap?to=${nativeTokenSymbol}`}>
          Swap
        </Link>{" "}
        or <ExternalLink href={JUMPER_BRIDGE_URL}>bridge</ExternalLink> {nativeTokenSymbol}.
      </Trans>
    </div>
  );
}

export function InsufficientWalletGasTokenBalanceMessage({ chainId }: { chainId: ContractsChainId }) {
  const gasPaymentTokens = getGasPaymentTokens(chainId);
  const localizedList = useLocalizedList(gasPaymentTokens.map((token) => getToken(chainId, token).symbol));
  const chainName = getChainName(chainId);
  const firstGasPaymentToken = gasPaymentTokens[0];

  return (
    <div>
      <Trans>
        Insufficient {localizedList} for gas on {chainName}.{" "}
        <Link
          className="underline underline-offset-2"
          to={`/trade/swap?to=${getToken(chainId, firstGasPaymentToken).symbol}`}
        >
          Swap
        </Link>{" "}
        or <ExternalLink href={JUMPER_BRIDGE_URL}>bridge</ExternalLink> {localizedList}.
      </Trans>
    </div>
  );
}

export function InsufficientGmxAccountGasTokenBalanceMessage({
  chainId,
  gasPaymentTokenAddress,
  onBeforeNavigation,
}: {
  chainId: ContractsChainId;
  gasPaymentTokenAddress?: string;
  onBeforeNavigation?: () => void;
}) {
  const { gasPaymentTokensText } = useGasPaymentTokensText(chainId);
  const [, setGmxAccountModalOpen] = useGmxAccountModalOpen();
  const [, setGmxAccountDepositViewTokenAddress] = useGmxAccountDepositViewTokenAddress();

  let tokensText = gasPaymentTokenAddress ? getToken(chainId, gasPaymentTokenAddress).symbol : gasPaymentTokensText;

  const handleDeposit = useCallback(() => {
    onBeforeNavigation?.();
    if (gasPaymentTokenAddress) {
      setGmxAccountDepositViewTokenAddress(convertTokenAddress(chainId, gasPaymentTokenAddress, "native"));
    }
    setGmxAccountModalOpen("deposit");
  }, [
    chainId,
    gasPaymentTokenAddress,
    onBeforeNavigation,
    setGmxAccountDepositViewTokenAddress,
    setGmxAccountModalOpen,
  ]);

  return (
    <div>
      <Trans>
        Insufficient {tokensText} for gas in your GMX Account{" "}
        <ColorfulButtonLink color="blue" onClick={handleDeposit}>
          Deposit {tokensText}
        </ColorfulButtonLink>
      </Trans>
    </div>
  );
}

export function InsufficientSourceChainNativeTokenBalanceMessage({
  srcChainId,
  onBeforeNavigation,
}: {
  srcChainId: SourceChainId;
  onBeforeNavigation?: () => void;
}) {
  const nativeToken = getViemChain(srcChainId).nativeCurrency;

  if (!nativeToken) {
    return null;
  }

  const nativeTokenSymbol = nativeToken.symbol;

  return (
    <div>
      <Trans>
        Insufficient {nativeTokenSymbol} for gas on {getChainName(srcChainId)}.{" "}
        <Link
          className="underline underline-offset-2"
          to={`/trade/swap?to=${nativeTokenSymbol}`}
          onClick={onBeforeNavigation}
        >
          Swap
        </Link>{" "}
        or <ExternalLink href={JUMPER_BRIDGE_URL}>bridge</ExternalLink> {nativeTokenSymbol}.
      </Trans>
    </div>
  );
}

export function ValidationBannerErrorContent({
  validationBannerErrorName,
  chainId,
  srcChainId,
  gasPaymentTokenAddress,
  onBeforeNavigation,
}: {
  validationBannerErrorName: ValidationBannerErrorName;
  chainId: ContractsChainId;
  srcChainId?: SourceChainId;
  gasPaymentTokenAddress?: string;
  onBeforeNavigation?: () => void;
}) {
  switch (validationBannerErrorName) {
    case ValidationBannerErrorName.insufficientNativeTokenBalance: {
      return <InsufficientNativeTokenBalanceMessage chainId={chainId} />;
    }
    case ValidationBannerErrorName.insufficientWalletGasTokenBalance: {
      return <InsufficientWalletGasTokenBalanceMessage chainId={chainId} />;
    }
    case ValidationBannerErrorName.insufficientGmxAccountSomeGasTokenBalance: {
      return <InsufficientGmxAccountGasTokenBalanceMessage chainId={chainId} onBeforeNavigation={onBeforeNavigation} />;
    }
    case ValidationBannerErrorName.insufficientSourceChainNativeTokenBalance: {
      if (!srcChainId) {
        return null;
      }

      return (
        <InsufficientSourceChainNativeTokenBalanceMessage
          srcChainId={srcChainId}
          onBeforeNavigation={onBeforeNavigation}
        />
      );
    }
    case ValidationBannerErrorName.insufficientGmxAccountWntBalance: {
      return <InsufficientWntBanner chainId={chainId} onBeforeNavigation={onBeforeNavigation} />;
    }
    case ValidationBannerErrorName.insufficientGmxAccountCurrentGasTokenBalance: {
      return (
        <InsufficientGmxAccountGasTokenBalanceMessage
          chainId={chainId}
          gasPaymentTokenAddress={gasPaymentTokenAddress}
          onBeforeNavigation={onBeforeNavigation}
        />
      );
    }
    case ValidationBannerErrorName.poolAtCapacity: {
      return (
        <div>
          <Trans>This pool is at maximum capacity and not accepting deposits right now. Try a different pool.</Trans>
        </div>
      );
    }
    default: {
      const _never: never = validationBannerErrorName;
      return null;
    }
  }
}
